import React from 'react';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { useThumbnailLocationListener } from '@state';
import { ScrollArea } from '../ScrollArea';
import { useViewerLayoutContext } from '../../contextProviders';

const noop = () => {};

const StudyBrowser = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
}: withAppTypes) => {
  const thumbnailLocation = useThumbnailLocationListener();
  const { isPatientMobile } = useViewerLayoutContext();
  const getTabContent = () => {
    const tabData = tabs.find(tab => tab.name === activeTabName);
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
    return tabData?.studies?.map(
      ({
        studyInstanceUid,
        date,
        description,
        numInstances,
        patientName,
        patientBirthday,
        patientSex,
        accessionNumber,
        modalities,
        displaySets,
      }) => {
        const isExpanded = expandedStudyInstanceUIDs.includes(studyInstanceUid);
        return (
          <div
            key={studyInstanceUid}
            className={
              isPatientMobile
                ? 'relative w-full min-w-full max-w-full flex-none snap-start overflow-hidden'
                : undefined
            }
          >
            <StudyItem
              servicesManager={servicesManager}
              date={date}
              description={description}
              numInstances={numInstances}
              patientName={patientName}
              patientBirthday={patientBirthday}
              patientSex={patientSex}
              accessionNumber={accessionNumber}
              isExpanded={isExpanded}
              displaySets={displaySets}
              modalities={modalities}
              isActive={isExpanded}
              onClick={() => onClickStudy(studyInstanceUid)}
              onClickThumbnail={onClickThumbnail}
              onDoubleClickThumbnail={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              data-cy="thumbnail-list"
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
              StudyMenuItems={StudyMenuItems}
              StudyInstanceUID={studyInstanceUid}
            />
          </div>
        );
      }
    );
  };

  const locationClass =
    thumbnailLocation === 'top'
      ? 'flex flex-col w-[135px] h-[135px] px-[8px] py-[10px] shrink-0 mt-0'
      : 'w-100 flex h-[48px] px-[8px] py-[10px]';

  return (
    <ScrollArea>
      <div
        className={
          thumbnailLocation === 'top'
            ? 'flex flex-1 flex-row gap-[4px] bg-[#27272b]'
            : isPatientMobile
              ? 'flex flex-1 touch-pan-x snap-x snap-mandatory flex-row flex-nowrap gap-[4px] overflow-x-auto bg-[#27272b]'
              : 'flex flex-1 flex-col gap-[4px] bg-[#27272b]'
        }
        data-cy={'studyBrowser-panel'}
      >
        {showSettings && (
          <div
            className={
              isPatientMobile
                ? 'hidden'
                : `${locationClass} items-center justify-center gap-[10px] bg-[#27272b]`
            }
          >
            <>
              <StudyBrowserViewOptions
                tabs={tabs}
                onSelectTab={onClickTab}
                activeTabName={activeTabName}
              />
              <StudyBrowserSort servicesManager={servicesManager} />
            </>
          </div>
        )}
        {getTabContent()}
      </div>
    </ScrollArea>
  );
};

export { StudyBrowser };
